import React from 'react';
import { toast } from 'react-toastify';
import { apiGet, apiPost, apiPatch } from '../common/amplifyApi';
import axios from 'axios';
import config from '../config';
import { mockOrder, mockFileUpload, mockOrdersList } from './mock';
import { getUniqueFilename, extractExtensionFrom } from '../common/helpers';
import { defaultOption } from '../pages/order/components/InvoicingDelivery';
import { impressionMethod, scanMethod } from '../pages/new-order/helpers/form';

var OrderStateContext = React.createContext();
var OrderDispatchContext = React.createContext();

const apiName = config.apiName;

const initialData = {
    findLoading: false,
    findError: false,
    fetchLoading: false,
    fetchError: false,
    saveLoading: false,
    saveError: false,
    currentOrder: null,
    // Patient photos upload
    photosUploadLoading: false,
    photosUploadError: false,
    photosUpload: null,
    photosDeleteLoading: false,
    photosDeleteError: false,
    // 3D file uploads (mandatory)
    maxillaryArchUploadLoading: false,
    maxillaryArchUploadError: false,
    maxillaryArchUpload: null,
    mandibularArchUploadLoading: false,
    mandibularArchUploadError: false,
    mandibularArchUpload: null,
    orders: null,
    orderUpdated: false,
    orderNeedsRefresh: false,
    search: {
        searchInput: '',
        orderStatus: '',
    },
    listPreferences: {
        order: 'desc',
        orderBy: 'createdAt',
        rowsPerPage: 50,
        page: 0,
    },
};

function orderReducer(state = initialData, { type, payload }) {
    switch (type) {
        case 'ORDER_RESET_STATE':
            return { ...initialData };
        case 'ORDER_FORM_RESET':
            return {
                ...state,
                saveLoading: false,
                saveError: false,
                photosUpload: null,
                currentOrder: null,
                photosDeleteLoading: false,
                photosDeleteError: false,
                maxillaryArchUploadLoading: false,
                maxillaryArchUploadError: false,
                maxillaryArchUpload: null,
                mandibularArchUploadLoading: false,
                mandibularArchUploadError: false,
                mandibularArchUpload: null,
            };
        case 'ORDER_FORM_FIND_STARTED':
            return { ...state, currentOrder: null, findLoading: true, findError: false };
        case 'ORDER_FORM_FIND_SUCCESS':
            return { ...state, currentOrder: payload, findLoading: false };
        case 'ORDER_FORM_FIND_ERROR':
            return { ...state, currentOrder: null, findLoading: false, findError: true };
        case 'ORDER_FORM_SAVE_STARTED':
            return { ...state, saveLoading: true, saveError: false };
        case 'ORDER_FORM_SAVE_SUCCESS':
            return { ...state, saveLoading: false, currentOrder: null, orders: null };
        case 'ORDER_FORM_SAVE_ERROR':
            return { ...state, saveLoading: false, saveError: true };
        case 'ORDER_LIST_FETCH_STARTED':
            return { ...state, fetchLoading: true, fetchError: false };
        case 'ORDER_LIST_FETCH_SUCCESS':
            return { ...state, orders: payload, fetchLoading: false };
        case 'ORDER_LIST_FETCH_ERROR':
            return { ...state, orders: null, fetchLoading: false, fetchError: true };
        case 'ORDER_LIST_CHANGE_PREFERENCES':
            return { ...state, listPreferences: payload };
        case 'ORDER_PHOTOS_UPLOAD_STARTED':
            return { ...state, photosUploadLoading: true, photosUploadError: false, photosUpload: null };
        case 'ORDER_PHOTOS_UPLOAD_SUCCESS':
            return { ...state, photosUploadLoading: false, photosUpload: payload };
        case 'ORDER_PHOTOS_UPLOAD_ERROR':
            return { ...state, photosUploadLoading: false, photosUploadError: true };
        case 'ORDER_PHOTOS_UPLOAD_DELETE':
            return { ...state, photosUpload: null };
        case 'ORDER_PHOTOS_DELETE_STARTED':
            return { ...state, photosDeleteLoading: true, photosDeleteError: false };
        case 'ORDER_PHOTOS_DELETE_SUCCESS':
            return { ...state, photosDeleteLoading: false, photosUpload: payload };
        case 'ORDER_PHOTOS_DELETE_ERROR':
            return { ...state, photosDeleteLoading: false, photosDeleteError: true };
        case 'ORDER_MAXILLARY_ARCH_UPLOAD_STARTED':
            return {
                ...state,
                maxillaryArchUploadLoading: true,
                maxillaryArchUploadError: false,
                maxillaryArchUpload: null,
            };
        case 'ORDER_MAXILLARY_ARCH_UPLOAD_SUCCESS':
            return { ...state, maxillaryArchUploadLoading: false, maxillaryArchUpload: payload };
        case 'ORDER_MAXILLARY_ARCH_UPLOAD_ERROR':
            return { ...state, maxillaryArchUploadLoading: false, maxillaryArchUploadError: true };
        case 'ORDER_MAXILLARY_ARCH_UPLOAD_DELETE':
            return { ...state, maxillaryArchUpload: null };
        case 'ORDER_MANDIBULAR_ARCH_UPLOAD_STARTED':
            return {
                ...state,
                mandibularArchUploadLoading: true,
                mandibularArchUploadError: false,
                mandibularArchUpload: null,
            };
        case 'ORDER_MANDIBULAR_ARCH_UPLOAD_SUCCESS':
            return { ...state, mandibularArchUploadLoading: false, mandibularArchUpload: payload };
        case 'ORDER_MANDIBULAR_ARCH_UPLOAD_ERROR':
            return { ...state, mandibularArchUploadLoading: false, mandibularArchUploadError: true };
        case 'ORDER_MANDIBULAR_ARCH_UPLOAD_DELETE':
            return { ...state, mandibularArchUpload: null };
        case 'ORDER_LIST_SEARCH':
            return { ...state, search: payload };
        case 'ORDER_UPDATE_INIT':
            return { ...state, orderUpdated: false, orderNeedsRefresh: false };
        case 'ORDER_UPDATE_STARTED':
            return { ...state, saveLoading: true, saveError: false };
        case 'ORDER_UPDATE_SUCCESS':
            return {
                ...state,
                saveLoading: false,
                currentOrder: { ...state.currentOrder, ...payload },
                orders: null,
                orderUpdated: true,
                orderNeedsRefresh: true,
            };
        case 'ORDER_REFRESH_COMPLETE':
            return { ...state, orderNeedsRefresh: false };
        case 'ORDER_UPDATE_ERROR':
            return { ...state, saveLoading: false, saveError: true };
        default:
            return state;
    }
}

function OrderProvider({ children }) {
    var [state, dispatch] = React.useReducer(orderReducer, initialData);

    return (
        <OrderStateContext.Provider value={state}>
            <OrderDispatchContext.Provider value={dispatch}>{children}</OrderDispatchContext.Provider>
        </OrderStateContext.Provider>
    );
}

function useOrderState() {
    var context = React.useContext(OrderStateContext);
    if (context === undefined) {
        throw new Error('useOrderState must be used within a OrderProvider');
    }
    return context;
}

function useOrderDispatch() {
    var context = React.useContext(OrderDispatchContext);
    if (context === undefined) {
        throw new Error('useOrderDispatch must be used within a OrderProvider');
    }
    return context;
}

function displayError(message, error) {
    console.log(error);
    const errorMsg = error.response?.data?.error || error.message;
    const finalMessage = errorMsg ? `${message}: ${errorMsg}` : message;
    toast.error(finalMessage);
}

// ###########################################################

const actions = {
    doResetState: () => async (dispatch) => {
        dispatch({ type: 'ORDER_RESET_STATE' });
    },

    doNewForm: () => async (dispatch) => {
        dispatch({ type: 'ORDER_FORM_RESET' });
    },

    doCreate: (values) => async (dispatch) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
        } else {
            try {
                dispatch({ type: 'ORDER_FORM_SAVE_STARTED' });
                await apiPost(apiName, `/v2/orders/3shape`, { body: values });
                dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
            } catch (error) {
                displayError('Unable to create order', error);
                dispatch({ type: 'ORDER_FORM_SAVE_ERROR' });
            }
        }
    },

    /**
     * Reset the property orderUpdated to false when opening the edit order page to avoid pushing back view order page
     */
    doInitUpdate: () => (dispatch) => {
        dispatch({ type: 'ORDER_UPDATE_INIT' });
    },

    doUpdate: (orderId, values) => async (dispatch) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_UPDATE_SUCCESS', payload: mockOrder });
        } else {
            try {
                dispatch({ type: 'ORDER_UPDATE_STARTED' });
                const updatedOrder = await apiPatch(apiName, `/v2/orders/${orderId}`, { body: values });
                dispatch({ type: 'ORDER_UPDATE_SUCCESS', payload: updatedOrder });
            } catch (error) {
                displayError('Unable to update order', error);
                dispatch({ type: 'ORDER_UPDATE_ERROR' });
            }
        }
    },

    doCancel: (orderId, practiceId) => async (dispatch, history) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
        } else {
            try {
                dispatch({ type: 'ORDER_FORM_SAVE_STARTED' });
                await apiPost(apiName, `/v2/orders/${orderId}/cancel`, { body: { practiceId: practiceId } });
                dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
                history.push('/app/order/list?orderStatus=in-progress');
                setTimeout(() => toast.success('Order is now cancelled'), 1000);
            } catch (error) {
                displayError('Unable to cancel order', error);
                dispatch({ type: 'ORDER_FORM_SAVE_ERROR' });
            }
        }
    },

    doComplete: (orderId, practiceId) => async (dispatch, history) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
        } else {
            try {
                dispatch({ type: 'ORDER_FORM_SAVE_STARTED' });
                await apiPost(apiName, `/v2/orders/${orderId}/complete`, {
                    body: { practiceId: practiceId },
                });
                dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
                history.push('/app/order/list?orderStatus=in-progress');
                setTimeout(() => toast.success('Order is now complete'), 1000);
            } catch (error) {
                displayError('Unable to complete order', error);
                dispatch({ type: 'ORDER_FORM_SAVE_ERROR' });
            }
        }
    },

    // this function is used to upload files for 3Shape orders.
    // The creation of 3Shape order must be maintained
    doUploadFileV1: (file, order) => async (dispatch) => {
        if (!file) {
            console.log('file to upload is undefined');
            dispatch({ type: 'ORDER_PHOTOS_UPLOAD_ERROR' });
        } else if (!config.isBackend) {
            const res = { ...mockFileUpload };
            delete res.signed_link;
            dispatch({ type: 'ORDER_PHOTOS_UPLOAD_SUCCESS', payload: { ...res, filename: file.name } });
        } else {
            try {
                dispatch({ type: 'ORDER_PHOTOS_UPLOAD_STARTED' });
                const mustUploadToFullContour = order.method === scanMethod && order.provideTreatmentPlan !== 'Yes';
                const uploadLinkEndpoint = mustUploadToFullContour
                    ? '/v2/fullcontour/upload-link'
                    : '/v2/attachment/upload-link';
                const fileExtension = extractExtensionFrom(file.name);
                const uniqueFilename = getUniqueFilename(order, fileExtension, mustUploadToFullContour);
                // get the upload link for the API
                const uploadLink = await apiPost(apiName, uploadLinkEndpoint, {
                    headers: { 'Content-Type': 'application/json' },
                    body: { filename: uniqueFilename },
                });
                if (uploadLink.data.signed_link !== mockFileUpload.signed_link) {
                    // signed_link is not mocked, upload the file to S3 (FullContour or SmileAcademy)
                    await axios.put(uploadLink.data.signed_link, file, {
                        headers: { 'Content-Type': 'application/zip' },
                    });
                }
                delete uploadLink.data.signed_link;
                let payload = { ...uploadLink.data, filename: file.name };
                if (order.provideTreatmentPlan === 'Yes') payload.treatmentPlanFile = uniqueFilename;
                if (order.method === impressionMethod) payload.patientPhotos = uniqueFilename;
                dispatch({ type: 'ORDER_PHOTOS_UPLOAD_SUCCESS', payload });
            } catch (error) {
                displayError('An error occurred trying to upload the file', error);
                dispatch({ type: 'ORDER_PHOTOS_UPLOAD_ERROR' });
            }
        }
    },

    doDeleteUploadedFileV1: () => async (dispatch) => {
        dispatch({ type: 'ORDER_PHOTOS_UPLOAD_DELETE' });
    },

    doFetch:
        (filter = null) =>
        async (dispatch) => {
            if (!config.isBackend) {
                dispatch({ type: 'ORDER_LIST_FETCH_STARTED' });
                setTimeout(() => {
                    dispatch({ type: 'ORDER_LIST_FETCH_SUCCESS', payload: mockOrdersList });
                }, 1000);
            } else {
                try {
                    dispatch({ type: 'ORDER_LIST_FETCH_STARTED' });
                    const data = await apiGet(apiName, '/v2/orders', filter ? { queryParams: filter } : undefined);
                    dispatch({ type: 'ORDER_LIST_FETCH_SUCCESS', payload: data });
                } catch (error) {
                    displayError('Unable to find orders', error);
                    dispatch({ type: 'ORDER_LIST_FETCH_ERROR' });
                }
            }
        },

    doDownloadFile: async (filekey) => {
        if (!filekey) return console.log('filekey to download is undefined');
        if (!config.isBackend) return;
        try {
            const res = await apiPost(apiName, '/v2/attachment/download-link', { body: { filename: filekey } });
            if (!res.signed_link) throw new Error('Invalid download link');
            window.open(res.signed_link);
        } catch (error) {
            displayError('An error occurred trying to download the file', error);
            console.log(error);
        }
    },

    doChangeListPreferences: (preferences) => (dispatch) => {
        dispatch({ type: 'ORDER_LIST_CHANGE_PREFERENCES', payload: preferences });
    },

    doSearchOrders: (search) => (dispatch) => {
        dispatch({ type: 'ORDER_LIST_SEARCH', payload: search });
    },

    doFind: (practiceId, orderId) => async (dispatch) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_FORM_FIND_STARTED' });
            setTimeout(() => {
                dispatch({ type: 'ORDER_FORM_FIND_SUCCESS', payload: mockOrder });
            }, 1000);
        } else {
            try {
                dispatch({ type: 'ORDER_FORM_FIND_STARTED' });
                const order = await apiGet(apiName, `/v2/orders/${orderId}`, {
                    queryParams: { practiceId: practiceId },
                });
                const formattedOrder = { ...order, invoicingOption: defaultOption, deliveryOption: defaultOption };
                dispatch({ type: 'ORDER_FORM_FIND_SUCCESS', payload: formattedOrder });
            } catch (error) {
                displayError('Unable to find the specified order', error);
                dispatch({ type: 'ORDER_FORM_FIND_ERROR' });
            }
        }
    },

    doCreateNewOrder: (values) => async (dispatch) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
        } else {
            try {
                dispatch({ type: 'ORDER_FORM_SAVE_STARTED' });
                await apiPost(apiName, `/v2/orders`, { body: values });
                dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
            } catch (error) {
                displayError('Unable to create order', error);
                dispatch({ type: 'ORDER_FORM_SAVE_ERROR' });
            }
        }
    },

    doFinalizeNewOrder: (orderId, values) => async (dispatch) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
        } else {
            try {
                dispatch({ type: 'ORDER_FORM_SAVE_STARTED' });
                await apiPatch(apiName, `/v2/orders/finalize/${orderId}`, {
                    body: values,
                    queryParams: { practiceId: values.practiceId },
                });
                dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
            } catch (error) {
                displayError('Unable to update order', error);
                dispatch({ type: 'ORDER_FORM_SAVE_ERROR' });
            }
        }
    },

    doRefineNewOrder: (orderId, values) => async (dispatch) => {
        if (!config.isBackend) {
            dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
        } else {
            try {
                dispatch({ type: 'ORDER_FORM_SAVE_STARTED' });
                await apiPost(apiName, `/v2/orders/${orderId}/refine`, { body: values });
                dispatch({ type: 'ORDER_FORM_SAVE_SUCCESS' });
            } catch (error) {
                displayError('Unable to create refinement', error);
                dispatch({ type: 'ORDER_FORM_SAVE_ERROR' });
            }
        }
    },

    // Generic file upload function that can handle any file type
    doUploadFile: (files, order, fileType, onProgress) => async (dispatch) => {
        if (!files || !files.length) {
            console.error(`No ${fileType} files provided for upload`);
            return dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_ERROR` });
        }

        if (!order?.serialNumber) {
            console.error(`Order serial number is required for ${fileType} upload`);
            return dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_ERROR` });
        }

        try {
            dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_STARTED` });

            // Report initial progress
            if (onProgress) {
                onProgress({
                    percentage: 5,
                });
            }

            // Step 1: Request signed upload URLs from backend for each file
            const fileInfoArray = files.map((file, index) => {
                let filename;
                let s3Key;

                if (fileType === 'PHOTOS') {
                    // For patient photos, use the original pattern from old implementation
                    const fileExtension = file.name.split('.').pop() || 'jpg';
                    filename = `photo${index + 1}.${fileExtension}`;
                    s3Key = `smileynova_orders/${order.serialNumber}/${filename}`;
                } else {
                    // For 3D files (MAXILLARY_ARCH/MANDIBULAR_ARCH)
                    const fileExtension = file.name.split('.').pop();
                    filename = `${fileType.toLowerCase()}.${fileExtension}`;
                    s3Key = `smileynova_orders/${order.serialNumber}/${filename}`;
                }

                return {
                    originalName: file.name,
                    contentType: fileType === 'PHOTOS' ? file.type : 'application/octet-stream',
                    size: file.size,
                    filename: s3Key, // Backend expects the full S3 key as filename
                    fileIndex: index,
                };
            });

            // Request signed URLs for all files
            const uploadLinksResponse = await apiPost(apiName, '/v2/attachment/upload-links', {
                headers: { 'Content-Type': 'application/json' },
                body: {
                    orderSerialNumber: order.serialNumber,
                    files: fileInfoArray,
                },
            });

            if (!uploadLinksResponse.uploadLinks || uploadLinksResponse.uploadLinks.length !== files.length) {
                throw new Error(`Failed to get upload links for ${fileType}`);
            }

            // Report progress after getting URLs
            if (onProgress) {
                onProgress({
                    percentage: 15,
                });
            }

            // Step 2: Upload all files in parallel for better performance
            const uploadResults = [];
            let completedUploads = 0;

            // Create upload promises for all files
            const uploadPromises = files.map(async (file, index) => {
                const uploadInfo = uploadLinksResponse.uploadLinks[index];

                // Upload to S3 using signed URL
                await axios.put(uploadInfo.signedUrl, file, {
                    headers: {
                        'Content-Type': fileType === 'PHOTOS' ? file.type : 'application/octet-stream',
                    },
                });

                completedUploads++;

                // Report progress for this file
                if (onProgress) {
                    // Calculate overall progress: 15% base + 85% for uploads
                    const overallProgress = 15 + (completedUploads / files.length) * 85;
                    onProgress({
                        percentage: Math.min(overallProgress, 95),
                    });
                }

                // Extract the actual filename (not the full s3Key)
                const actualFilename = uploadInfo.s3Key?.split('/').pop() || uploadInfo.filename?.split('/').pop();

                return {
                    originalName: file.name,
                    filename: actualFilename,
                    s3Key: uploadInfo.s3Key || `smileynova_orders/${order.serialNumber}/${actualFilename}`,
                    contentType: fileType === 'PHOTOS' ? file.type : 'application/octet-stream',
                    size: file.size,
                };
            });

            // Wait for all uploads to complete
            const results = await Promise.all(uploadPromises);
            uploadResults.push(...results);

            // Final progress update
            if (onProgress) {
                onProgress({
                    percentage: 100,
                });
            }

            // Step 3: Update state with successful uploads
            const payload = {
                uploadedFiles: uploadResults,
                orderSerialNumber: order.serialNumber,
                totalFiles: files.length,
                uploadedAt: new Date().toISOString(),
                fileType: fileType,
            };

            dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_SUCCESS`, payload });
        } catch (error) {
            displayError(`Failed to upload ${fileType}`, error);
            dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_ERROR` });
        }
    },

    // Generic file delete function
    doDeleteUploadedFile: (fileToDelete, order, fileType) => async (dispatch, getState) => {
        if (!fileToDelete) {
            console.log(`${fileType} file to delete is undefined`);
            return dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_ERROR` });
        }

        if (!order?.serialNumber) {
            console.log(`Order serial number is required for ${fileType} deletion`);
            return dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_ERROR` });
        }

        if (!config.isBackend) {
            // Mock successful deletion for development
            dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_DELETE` });
            return;
        }

        try {
            // Step 1: Request signed DELETE URL from backend
            const deleteRequest = {
                orderSerialNumber: order.serialNumber,
                s3Key: fileToDelete.s3Key || `smileynova_orders/${order.serialNumber}/${fileToDelete.filename}`,
                originalName: fileToDelete.originalName,
            };

            // Request signed DELETE URL
            const deleteResponse = await apiPost(apiName, '/v2/attachment/delete-link', {
                headers: { 'Content-Type': 'application/json' },
                body: deleteRequest,
            });

            if (!deleteResponse.deleteLink?.signedUrl) {
                throw new Error(`Failed to get delete link for ${fileType}`);
            }

            // Step 2: Delete file from S3 using signed DELETE URL
            await axios.delete(deleteResponse.deleteLink.signedUrl);

            // Step 3: Update state by removing the deleted file
            dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_DELETE` });
        } catch (error) {
            displayError(`Failed to delete ${fileType}`, error);
            dispatch({ type: `ORDER_${fileType.toUpperCase()}_UPLOAD_ERROR` });
        }
    },

    // Convenience methods for specific file types
    doUploadPatientPhotos: (files, order, onProgress) => async (dispatch) => {
        return actions.doUploadFile(files, order, 'PHOTOS', onProgress)(dispatch);
    },

    doDeletePatientPhoto: (fileToDelete, order) => async (dispatch, getState) => {
        return actions.doDeleteUploadedFile(fileToDelete, order, 'PHOTOS')(dispatch, getState);
    },

    doUploadMaxillaryArch: (files, order, onProgress) => async (dispatch) => {
        return actions.doUploadFile(files, order, 'MAXILLARY_ARCH', onProgress)(dispatch);
    },

    doDeleteMaxillaryArch: (fileToDelete, order) => async (dispatch, getState) => {
        return actions.doDeleteUploadedFile(fileToDelete, order, 'MAXILLARY_ARCH')(dispatch, getState);
    },

    doUploadMandibularArch: (files, order, onProgress) => async (dispatch) => {
        return actions.doUploadFile(files, order, 'MANDIBULAR_ARCH', onProgress)(dispatch);
    },

    doDeleteMandibularArch: (fileToDelete, order) => async (dispatch, getState) => {
        return actions.doDeleteUploadedFile(fileToDelete, order, 'MANDIBULAR_ARCH')(dispatch, getState);
    },
};

export { OrderProvider, useOrderState, useOrderDispatch, actions };
